import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Input } from 'react-native-elements';
import { TouchableOpacity, KeyboardTypeOptions } from 'react-native';
import Icon from '@expo/vector-icons/FontAwesome';
import { Text } from '@/components/Text';
import { isPatientApp } from '@/config/constants';
import { Input as TherapistInput } from '@/components/ui/therapist/Input';
import { EmailInput } from '@/screens/Login/EmailInput';
import { PasswordInput } from '@/screens/Login/PasswordInput';
import colors from '@/config/colors';

interface SharedFormFieldProps {
  control: any;
  name: string;
  label: string;
  rules?: any;
  secureTextEntry?: boolean;
  keyboardType?: KeyboardTypeOptions;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  placeholder?: string;
  isPasswordField?: boolean;
  useEmailInput?: boolean;
  usePasswordInput?: boolean;
  additionalInfo?: string;
  containerStyle?: object;
  inputContainerStyle?: object;
  errorMessageStyle?: object;
}

export const SharedFormField = ({
  control,
  name,
  label,
  rules,
  secureTextEntry = false,
  keyboardType = 'default',
  autoCapitalize = 'none',
  placeholder,
  useEmailInput = false,
  usePasswordInput = false,
  additionalInfo,
  containerStyle,
  inputContainerStyle,
  errorMessageStyle,
}: SharedFormFieldProps) => {
  const [visible, setVisible] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        if (isPatientApp) {
          if (useEmailInput) {
            return <EmailInput field={field} fieldState={fieldState} />;
          }
          if (usePasswordInput) {
            return (
              <PasswordInput
                field={field}
                placeholder={placeholder || label}
                fieldState={fieldState}
              />
            );
          }
          return (
            <>
              <Input
                autoCapitalize={autoCapitalize}
                placeholder={placeholder || label}
                placeholderTextColor={colors.medium}
                secureTextEntry={secureTextEntry ? !visible : null}
                keyboardType={keyboardType}
                value={field.value}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                rightIcon={
                  secureTextEntry ? (
                    <TouchableOpacity onPress={() => setVisible((v) => !v)}>
                      <Icon
                        name={visible ? 'eye' : 'eye-slash'}
                        size={28}
                        color={colors.primary}
                      />
                    </TouchableOpacity>
                  ) : undefined
                }
                containerStyle={containerStyle}
                inputContainerStyle={inputContainerStyle}
              />
              {!!fieldState.error && (
                <Text style={errorMessageStyle}>
                  {fieldState.error.message}
                </Text>
              )}
            </>
          );
        }

        return (
          <TherapistInput
            label={label}
            required={!!rules?.required}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={placeholder}
            secureTextEntry={secureTextEntry}
            additionalInfo={additionalInfo}
            status={
              fieldState.error
                ? 'warning'
                : field.value?.length > 0
                  ? 'validated'
                  : 'default'
            }
            statusMessage={fieldState.error?.message}
          />
        );
      }}
    />
  );
};
